import { useState, useRef, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CaretDownIcon,
  EnvelopeClosedIcon,
  LockClosedIcon,
  PersonIcon,
  PlusIcon,
} from '@radix-ui/react-icons';
import { ShieldCheckIcon } from '@phosphor-icons/react';
import { Button, DropdownMenu } from '@radix-ui/themes';
import { useAuthStore, selectCanAccessAdmin, type User } from '../../../stores';
import { useGet } from '../../../hooks';
import { useTranslation, type MessageKey } from '../../../i18n';
import { authFetch } from '../../../util/authFetch';
import { LanguageSelector } from '../../LanguageSelector';
import { ThemeToggle } from '../../ThemeToggle';
import './UserMenu.css';

interface CreditsResponse {
  currentCredits: number;
  transactions: CreditTransactionResponse[];
}

interface CreditTransactionResponse {
  id: number;
  amount: number;
  transactionType: string;
  description: string;
  createdAt: string;
}

interface CryptoDepositOption {
  id: number;
  name: string;
  symbol: string;
  network: string;
  address: string;
  qrCodeUrl?: string;
  creditsPerUnit: number;
  description?: string;
}

interface CreditPurchaseOption {
  id: number;
  providerType: string;
  name: string;
  credits: number;
  priceAmount: number;
  currency: string;
  description?: string;
  buyButtonId: string;
  publishableKey: string;
}

function formatPrice(amount: number, currency: string, locale: string) {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount} ${currency.toUpperCase()}`;
  }
}

function StripeBuyButton({
  buyButtonId,
  publishableKey,
  clientReferenceId,
}: {
  buyButtonId: string;
  publishableKey: string;
  clientReferenceId: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.innerHTML = '';
    const btn = document.createElement('stripe-buy-button');
    btn.setAttribute('buy-button-id', buyButtonId);
    btn.setAttribute('publishable-key', publishableKey);
    btn.setAttribute('client-reference-id', clientReferenceId);
    el.appendChild(btn);

    return () => {
      el.innerHTML = '';
    };
  }, [buyButtonId, publishableKey, clientReferenceId]);

  return <div className="credits-stripe-buy-button" ref={containerRef} />;
}

type UserMenuTab = 'credits' | 'account' | 'preferences';

const USER_MENU_TAB_KEYS: { id: UserMenuTab; labelKey: MessageKey }[] = [
  { id: 'credits', labelKey: 'userMenu.tabs.credits' },
  { id: 'account', labelKey: 'userMenu.tabs.account' },
  { id: 'preferences', labelKey: 'userMenu.tabs.preferences' },
];

function formatTransactionAmount(amount: number) {
  return amount > 0 ? `+${amount}` : `${amount}`;
}

function formatTransactionDate(value: string, locale: string) {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;
  return parsedDate.toLocaleString(locale);
}

function UserMenuCreditsTab({
  isOpen,
  userId,
  currentCredits,
  loading,
  transactions,
}: {
  isOpen: boolean;
  userId: string;
  currentCredits: number | undefined;
  loading: boolean;
  transactions: CreditTransactionResponse[];
}) {
  const { t, locale } = useTranslation();
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showTether, setShowTether] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowAddPanel(false);
      setShowTether(false);
    }
  }, [isOpen]);

  const { data: purchaseOptions, loading: purchaseLoading } = useGet<CreditPurchaseOption[]>(
    isOpen && showAddPanel ? '/api/user/credits/purchase-options' : null,
    { enabled: isOpen && showAddPanel }
  );
  const stripePlans = (purchaseOptions ?? []).filter(
    (option) => option.providerType === 'stripe' && option.buyButtonId && option.publishableKey
  );

  const { data: depositOptions, loading: depositLoading } = useGet<CryptoDepositOption[]>(
    isOpen && showAddPanel ? '/api/user/credits/deposit-options?symbol=USDT' : null,
    { enabled: isOpen && showAddPanel }
  );
  const usdtWallets = depositOptions ?? [];

  const copyAddress = (addr: string) => {
    navigator.clipboard.writeText(addr).then(() => {});
  };

  return (
    <div className="user-menu-credits">
      <div className="credits-current-balance">
        <div className="credits-balance-label">{t('userMenu.credits.currentBalance')}</div>
        <div className="credits-balance-value-row">
          <div className="credits-balance-value">
            {loading || currentCredits === undefined ? (
              <span className="credits-balance-loading">{t('common.loading')}</span>
            ) : (
              <>
                <span>{currentCredits.toLocaleString(locale)}</span>
                <span className="credits-balance-unit"> {t('userMenu.credits.unit')}</span>
              </>
            )}
          </div>
          <button
            type="button"
            className={`credits-add-button ${showAddPanel ? 'active' : ''}`}
            onClick={() => setShowAddPanel((open) => !open)}
            aria-expanded={showAddPanel}
            aria-label={
              showAddPanel ? t('userMenu.credits.hideAddOptions') : t('userMenu.credits.add')
            }
            title={showAddPanel ? t('userMenu.credits.hideAdd') : t('userMenu.credits.add')}
          >
            <PlusIcon aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className={`credits-add-panel ${showAddPanel ? 'open' : ''} ${showTether ? 'tether-open' : ''}`}>
        <div className="credits-add-panel-inner">
          <div className="credits-stripe-section">
            {purchaseLoading && (
              <div className="credits-empty-state">{t('userMenu.credits.loadingPurchase')}</div>
            )}
            {!purchaseLoading && stripePlans.length === 0 && (
              <div className="credits-empty-state">{t('userMenu.credits.noCardOptions')}</div>
            )}
            {!purchaseLoading &&
              stripePlans.map((plan) => (
                <div key={plan.id} className="credits-stripe-option">
                  <div className="credits-stripe-option-header">
                    <span className="credits-stripe-option-name">{plan.name}</span>
                    <span className="credits-stripe-option-meta">
                      {t('userMenu.credits.planCredits', {
                        count: plan.credits.toLocaleString(locale),
                      })}
                      {plan.priceAmount > 0 &&
                        ` · ${formatPrice(plan.priceAmount, plan.currency, locale)}`}
                    </span>
                    {plan.description && (
                      <span className="credits-stripe-option-description">{plan.description}</span>
                    )}
                  </div>
                  <StripeBuyButton
                    buyButtonId={plan.buyButtonId}
                    publishableKey={plan.publishableKey}
                    clientReferenceId={userId}
                  />
                </div>
              ))}
          </div>

          <div className="credits-tether-section">
            <button
              type="button"
              className="credits-tether-toggle"
              onClick={() => setShowTether((v) => !v)}
            >
              <span className="credits-tether-icon">₿</span>
              {t('userMenu.credits.loadWithTether')}
              <span className="credits-tether-chevron">{showTether ? '▼' : '▶'}</span>
            </button>
            {showTether && (
              <div className="credits-tether-content">
                {depositLoading ? (
                  <div className="credits-empty-state">{t('userMenu.credits.loadingDeposit')}</div>
                ) : usdtWallets.length === 0 ? (
                  <div className="credits-empty-state">{t('userMenu.credits.noUsdtWallet')}</div>
                ) : (
                  usdtWallets.map((w) => (
                    <div key={w.id} className="credits-tether-wallet">
                      <div className="credits-tether-wallet-name">
                        {w.name} ({w.network})
                      </div>
                      <div className="credits-tether-wallet-rate">
                        {t('userMenu.credits.rate', {
                          symbol: w.symbol,
                          credits: w.creditsPerUnit,
                        })}
                      </div>
                      <div className="credits-tether-address-row">
                        <code className="credits-tether-address">{w.address}</code>
                        <button
                          type="button"
                          className="credits-tether-copy"
                          onClick={() => copyAddress(w.address)}
                          title={t('userMenu.credits.copyAddress')}
                        >
                          {t('common.copy')}
                        </button>
                      </div>
                      {w.description && (
                        <div className="credits-tether-desc">{w.description}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="credits-transactions-section">
        <div className="credits-transactions-title">{t('userMenu.credits.recentTransactions')}</div>
        <div className="credits-transactions-list">
          {loading && (
            <div className="credits-empty-state">{t('userMenu.credits.loadingTransactions')}</div>
          )}
          {!loading && transactions.length === 0 && (
            <div className="credits-empty-state">{t('userMenu.credits.noTransactions')}</div>
          )}
          {!loading &&
            transactions.map((transaction) => (
              <div key={transaction.id} className="credits-transaction-item">
                <div className="credits-transaction-top">
                  <span className="credits-transaction-type">{transaction.transactionType}</span>
                  <span
                    className={`credits-transaction-amount ${transaction.amount >= 0 ? 'positive' : 'negative'}`}
                  >
                    {formatTransactionAmount(transaction.amount)}
                  </span>
                </div>
                <div className="credits-transaction-description">{transaction.description}</div>
                <div className="credits-transaction-time">
                  {formatTransactionDate(transaction.createdAt, locale)}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function UserMenuAccountTab({
  user,
  currentCredits,
  loading,
  transactions,
}: {
  user: User;
  currentCredits: number | undefined;
  loading: boolean;
  transactions: CreditTransactionResponse[];
}) {
  const { t, locale } = useTranslation();
  const totalEarned = transactions.reduce((sum, item) => (item.amount > 0 ? sum + item.amount : sum), 0);
  const totalSpent = transactions.reduce(
    (sum, item) => (item.amount < 0 ? sum + Math.abs(item.amount) : sum),
    0
  );

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(null);
    setPasswordBusy(false);
  };

  const handleCancelPassword = () => {
    resetPasswordForm();
    setShowPasswordForm(false);
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!newPassword.trim()) {
      setPasswordError(t('userMenu.account.errors.newRequired'));
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError(t('userMenu.account.errors.minLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError(t('userMenu.account.errors.mismatch'));
      return;
    }

    setPasswordBusy(true);
    try {
      const response = await authFetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmPassword,
        }),
      });

      const data = (await response.json().catch(() => null)) as { message?: string } | null;
      if (!response.ok) {
        setPasswordError(data?.message ?? t('userMenu.account.errors.failed'));
        return;
      }

      setPasswordSuccess(data?.message ?? t('userMenu.account.success'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('[UserMenu] Change password failed:', err);
      setPasswordError(t('userMenu.account.errors.failed'));
    } finally {
      setPasswordBusy(false);
    }
  };

  return (
    <div className="user-menu-account">
      <div className="user-menu-account-profile">
        <div className="user-menu-account-avatar" aria-hidden="true">
          {(user.name || user.email).charAt(0).toUpperCase()}
        </div>
        <div className="user-menu-account-info">
          <div className="user-menu-account-name">{user.fullName}</div>
          <div className="user-menu-account-email">{user.email}</div>
          {user.role && <div className="user-menu-role">{user.role}</div>}
        </div>
      </div>

      <div className="user-menu-account-stats">
        <div className="user-menu-account-stats-title">{t('userMenu.account.creditUsage')}</div>
        <div className="user-menu-account-stats-grid">
          <div className="user-menu-account-stat">
            <span className="user-menu-account-stat-label">{t('userMenu.account.balance')}</span>
            <span className="user-menu-account-stat-value positive">
              {loading || currentCredits === undefined ? '…' : currentCredits.toLocaleString(locale)}
            </span>
          </div>
          <div className="user-menu-account-stat">
            <span className="user-menu-account-stat-label">{t('userMenu.account.earned')}</span>
            <span className="user-menu-account-stat-value positive">
              {loading ? '…' : totalEarned.toLocaleString(locale)}
            </span>
          </div>
          <div className="user-menu-account-stat">
            <span className="user-menu-account-stat-label">{t('userMenu.account.spent')}</span>
            <span className="user-menu-account-stat-value negative">
              {loading ? '…' : totalSpent.toLocaleString(locale)}
            </span>
          </div>
          <div className="user-menu-account-stat">
            <span className="user-menu-account-stat-label">{t('userMenu.account.transactions')}</span>
            <span className="user-menu-account-stat-value">
              {loading ? '…' : transactions.length.toLocaleString(locale)}
            </span>
          </div>
        </div>
      </div>

      <div className="user-menu-password">
        {!showPasswordForm ? (
          <button
            type="button"
            className="user-menu-password-toggle"
            onClick={() => {
              resetPasswordForm();
              setShowPasswordForm(true);
            }}
          >
            <LockClosedIcon aria-hidden="true" className="user-menu-password-toggle-icon" />
            {t('userMenu.account.changePassword')}
          </button>
        ) : (
          <form
            className="user-menu-password-form"
            onSubmit={(e) => void handleChangePassword(e)}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <div className="user-menu-account-stats-title">
              {t('userMenu.account.changePasswordTitle')}
            </div>
            <label className="user-menu-password-field">
              <span>{t('userMenu.account.currentPassword')}</span>
              <input
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t('userMenu.account.currentPasswordHint')}
                disabled={passwordBusy}
              />
            </label>
            <label className="user-menu-password-field">
              <span>{t('userMenu.account.newPassword')}</span>
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                disabled={passwordBusy}
              />
            </label>
            <label className="user-menu-password-field">
              <span>{t('userMenu.account.confirmPassword')}</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                disabled={passwordBusy}
              />
            </label>
            {passwordError && (
              <div className="user-menu-password-message error">{passwordError}</div>
            )}
            {passwordSuccess && (
              <div className="user-menu-password-message success">{passwordSuccess}</div>
            )}
            <div className="user-menu-password-actions">
              <button
                type="button"
                className="user-menu-password-cancel"
                onClick={handleCancelPassword}
                disabled={passwordBusy}
              >
                {t('common.cancel')}
              </button>
              <button type="submit" className="user-menu-password-submit" disabled={passwordBusy}>
                {passwordBusy ? t('userMenu.account.saving') : t('userMenu.account.updatePassword')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function UserMenuPreferencesTab() {
  return (
    <div className="user-menu-preferences">
      <ThemeToggle variant="menu" className="user-menu-section" />
    </div>
  );
}

export function UserMenu() {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const { user, accessToken, logout } = useAuthStore();
  const canAccessAdmin = useAuthStore(selectCanAccessAdmin);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<UserMenuTab>('credits');
  const { data: creditsData, loading: creditsLoading } = useGet<CreditsResponse>(
    user ? '/api/user/credits' : null,
    { enabled: !!user }
  );
  const currentCredits = creditsData?.currentCredits;
  const transactions = creditsData?.transactions ?? [];

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      logout();
      navigate('/login');
    }
  };

  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open);
    if (!open) {
      setActiveTab('credits');
    }
  };

  const handleAdminClick = () => {
    setMenuOpen(false);
    navigate('/admin');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="user-menu user-menu-root">
      <DropdownMenu.Root open={menuOpen} onOpenChange={handleMenuOpenChange} modal={false}>
        <DropdownMenu.Trigger>
          <Button
            variant="surface"
            radius="none"
            size="3"
            className={`user-menu-button ${menuOpen ? 'active' : ''}`}
            aria-label={t('userMenu.ariaLabel')}
          >
            <PersonIcon aria-hidden="true" />
            <span className="text-sm">{user.name || user.email}</span>
            <span
              className="user-menu-credits-badge"
              aria-label={
                creditsLoading || currentCredits === undefined
                  ? t('userMenu.loadingCredits')
                  : t('userMenu.creditsCount', { count: currentCredits })
              }
            >
              {creditsLoading || currentCredits === undefined
                ? '…'
                : currentCredits.toLocaleString(locale)}
            </span>
            <CaretDownIcon aria-hidden="true" className="ml-2" />
          </Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          className="user-menu-dropdown"
          variant="soft"
          align="end"
          sideOffset={4}
          collisionPadding={12}
        >
          <div className="user-menu-header">
            <div className="user-menu-header-main">
              <div className="user-menu-name flex items-center gap-2">
                <PersonIcon aria-hidden="true" />
                <span className="text-sm">{user.fullName}</span>
              </div>
              <div className="user-menu-email flex items-center gap-2">
                <EnvelopeClosedIcon aria-hidden="true" />
                <span className="text-xs">{user.email}</span>
              </div>
              {user.role && <div className="user-menu-role">{user.role}</div>}
            </div>
            <div className="user-menu-header-actions" role="group" aria-label={t('userMenu.ariaLabel')}>
              <LanguageSelector variant="compact" className="user-menu-header-language" />
              {canAccessAdmin && (
                <button
                  type="button"
                  className="user-menu-admin-button"
                  onClick={handleAdminClick}
                  title={t('userMenu.administration')}
                >
                  <ShieldCheckIcon size={14} weight="bold" className="user-menu-admin-icon" aria-hidden="true" />
                  <span>{t('userMenu.administration')}</span>
                </button>
              )}
            </div>
          </div>

          <div className="user-menu-tabs">
            <div
              className="user-menu-tabs-list"
              role="tablist"
              aria-label={t('userMenu.sectionsAria')}
            >
              {USER_MENU_TAB_KEYS.map(({ id, labelKey }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === id}
                  className={`user-menu-tab-trigger${activeTab === id ? ' active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setActiveTab(id)}
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>

            <div className="user-menu-tab-content">
              {activeTab === 'credits' && (
                <div className="user-menu-tab-panel" role="tabpanel">
                  <UserMenuCreditsTab
                    isOpen={menuOpen}
                    userId={user.id}
                    currentCredits={currentCredits}
                    loading={creditsLoading}
                    transactions={transactions}
                  />
                </div>
              )}
              {activeTab === 'account' && (
                <div className="user-menu-tab-panel" role="tabpanel">
                  <UserMenuAccountTab
                    user={user}
                    currentCredits={currentCredits}
                    loading={creditsLoading}
                    transactions={transactions}
                  />
                </div>
              )}
              {activeTab === 'preferences' && (
                <div className="user-menu-tab-panel" role="tabpanel">
                  <UserMenuPreferencesTab />
                </div>
              )}
            </div>
          </div>

          <DropdownMenu.Separator className="user-menu-radix-separator" />
          <DropdownMenu.Item
            className="user-menu-radix-item user-menu-signout-item"
            color="gray"
            onSelect={() => void handleLogout()}
          >
            {t('userMenu.signOut')}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </div>
  );
}
